import { ApiRequestError, apiClient } from '@/api/client';
import { envelopeMessage } from '@/api/envelope';
import { http } from '@/api/http';
import { DEFAULT_TEAM_COUNTRY, normalizeTeamCountry, type TeamCountryCode } from '@/constants/teamCountries';
import { fetchAvailableServicersPage, type AvailableServicer } from './order';
import { parsePostalCodeList } from './user';

/** 首页定价区展示的订阅方案（由 `get_sub_price` 归一化）。 */
export interface SubPricePlan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  /** 按年付费时展示的「每月」价格 */
  annualMonthlyPrice: number;
  features: string[];
  badge: string | null;
  popular: boolean;
  borderClass: string;
  cta: string;
  ctaClass: string;
}

/** 当前团队成员配额（`get_sub_price.team_capacity`，未入团时为 null）。 */
export interface TeamMemberCapacity {
  baseMembers: number;
  extraMembers: number;
  totalCapacity: number;
  currentCount: number;
  remaining: number;
}

/** `/system/team/get_sub_price` 返回的基础套餐 + 增购席位定价。 */
export interface TeamSubPrice {
  priceMonth: number;
  priceYear: number;
  basePackagePriceMonth: number;
  basePackagePriceYear: number;
  baseMemberCount: number;
  extraMemberPriceMonth: number;
  extraMemberPriceYear: number;
  extraMembers: number;
  teamCapacity: TeamMemberCapacity | null;
}

interface GetSubPriceHttpResponse {
  error?: boolean;
  message?: string;
  data?: unknown;
}

function payloadRecord(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== 'object') return {};
  return data as Record<string, unknown>;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function parseTeamCapacity(raw: unknown): TeamMemberCapacity | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const baseMembers = readNum(o, ['base_members', 'baseMembers']) ?? 0;
  const extraMembers = readNum(o, ['extra_members', 'extraMembers']) ?? 0;
  const totalCapacity =
    readNum(o, ['total_capacity', 'totalCapacity']) ?? baseMembers + extraMembers;
  const currentCount = readNum(o, ['current_count', 'currentCount']) ?? 0;
  const remaining =
    readNum(o, ['remaining']) ?? Math.max(0, totalCapacity - currentCount);
  return { baseMembers, extraMembers, totalCapacity, currentCount, remaining };
}

function countryLookupKeys(country: TeamCountryCode): string[] {
  switch (country) {
    case 'AU':
      return ['AU', 'au', 'AUS', 'AUD'];
    case 'NZ':
      return ['NZ', 'nz', 'NZL', 'NZD'];
    case 'CA':
      return ['CA', 'ca', 'CAN', 'CAD'];
    case 'UK':
      return ['UK', 'uk', 'GB', 'gb', 'GBP'];
    case 'US':
      return ['US', 'us', 'USA', 'USD'];
  }
}

function recordMatchesCountry(item: unknown, country: TeamCountryCode): boolean {
  if (!item || typeof item !== 'object') return false;
  const rec = item as Record<string, unknown>;
  const raw = rec.country ?? rec.country_code ?? rec.countryCode ?? rec.code;
  if (typeof raw !== 'string' && typeof raw !== 'number') return false;
  return normalizeTeamCountry(String(raw)) === country;
}

/** 从按国家区分的价格结构中取出指定国家的固定价（不做汇率换算）。 */
function pickCountryPriceData(data: unknown, country: TeamCountryCode): unknown {
  if (!data || typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    const row = data.find((item) => recordMatchesCountry(item, country));
    return row ?? data[0] ?? data;
  }

  const o = data as Record<string, unknown>;
  const hasDirectPrice =
    readNum(o, [
      'price_month',
      'monthly_price',
      'monthlyPrice',
      'base_package_price_month',
      'basePackagePriceMonth',
    ]) !== undefined;
  if (hasDirectPrice) return data;

  for (const nestKey of ['prices', 'countries', 'country_prices', 'countryPrices', 'list', 'items']) {
    const nested = o[nestKey];
    if (!nested) continue;
    const picked = pickCountryPriceData(nested, country);
    if (picked && typeof picked === 'object' && !Array.isArray(picked)) {
      return { ...o, ...(picked as Record<string, unknown>) };
    }
  }

  for (const key of countryLookupKeys(country)) {
    const nested = o[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return { ...o, ...(nested as Record<string, unknown>) };
    }
  }

  return data;
}

/** 解析基础套餐价、增购单价与团队配额（支持嵌套在 `data` 内）。 */
export function parseTeamSubPrice(data: unknown): TeamSubPrice | null {
  const o = payloadRecord(data);
  const month = readNum(o, ['price_month', 'monthly_price', 'monthlyPrice']);
  const year = readNum(o, ['price_year', 'yearly_price', 'yearlyPrice']);
  const baseMonth = readNum(o, ['base_package_price_month', 'basePackagePriceMonth']);
  const baseYear = readNum(o, ['base_package_price_year', 'basePackagePriceYear']);
  if (month === undefined && year === undefined && baseMonth === undefined) return null;

  const extraMemberPriceMonth =
    readNum(o, ['extra_member_price_month', 'extraMemberPriceMonth']) ?? 0;
  const extraMemberPriceYear =
    readNum(o, ['extra_member_price_year', 'extraMemberPriceYear']) ??
    roundMoney(extraMemberPriceMonth * 12);
  const extraMembers = readNum(o, ['extra_members', 'extraMembers']) ?? 0;
  const baseMemberCount = readNum(o, ['base_member_count', 'baseMemberCount']) ?? 0;

  const basePackagePriceMonth = baseMonth ?? month ?? (year !== undefined ? year / 12 : 0);
  const basePackagePriceYear = baseYear ?? roundMoney(basePackagePriceMonth * 12);
  const priceMonth = month ?? roundMoney(basePackagePriceMonth + extraMemberPriceMonth * extraMembers);
  const priceYear = year ?? roundMoney(basePackagePriceYear + extraMemberPriceYear * extraMembers);

  return {
    priceMonth,
    priceYear,
    basePackagePriceMonth,
    basePackagePriceYear,
    baseMemberCount,
    extraMemberPriceMonth,
    extraMemberPriceYear,
    extraMembers,
    teamCapacity: parseTeamCapacity(o.team_capacity ?? o.teamCapacity),
  };
}

/** 当前团队应付月/年价 = 基础套餐 + 已增购席位 × 单价。 */
export function currentTeamPlanPrice(price: TeamSubPrice): { month: number; year: number } {
  const extra = price.teamCapacity?.extraMembers ?? price.extraMembers;
  return {
    month: roundMoney(price.basePackagePriceMonth + price.extraMemberPriceMonth * extra),
    year: roundMoney(price.basePackagePriceYear + price.extraMemberPriceYear * extra),
  };
}

/** 按增购席位数预览套餐总价（不含当前周期剩余月数折算）。 */
export function previewTeamPlanPrice(
  price: TeamSubPrice,
  extraMembers: number,
): { month: number; year: number } {
  const extra = Math.max(0, extraMembers);
  return {
    month: roundMoney(price.basePackagePriceMonth + price.extraMemberPriceMonth * extra),
    year: roundMoney(price.basePackagePriceYear + price.extraMemberPriceYear * extra),
  };
}

function readString(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  }
  return undefined;
}

function uniqueCodes(codes: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of codes) {
    const code = raw.trim();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

function codesFromUnknownList(raw: unknown): string[] {
  const fromSimple = parsePostalCodeList(raw);
  if (fromSimple.length > 0) return fromSimple;
  if (!Array.isArray(raw)) return [];
  const codes: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string' || typeof item === 'number') {
      codes.push(String(item));
      continue;
    }
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const code =
      readString(rec, ['postal_code', 'postalCode', 'code', 'city', 'name']) ?? '';
    if (code) codes.push(code);
  }
  return uniqueCodes(codes);
}

/** 成员负责区域：列表字段、单个邮编、或 city 文本 */
function parseMemberPostalCodes(o: Record<string, unknown>): string[] {
  for (const key of [
    'postal_code_lst',
    'postal_codes',
    'postalCodes',
    'service_areas',
    'serviceAreas',
    'areas',
  ]) {
    const parsed = codesFromUnknownList(o[key]);
    if (parsed.length > 0) return parsed;
  }

  const single = readString(o, ['postal_code', 'postalCode']);
  if (single) {
    return uniqueCodes(single.split(/[,;|\/]+/));
  }

  const city = readString(o, ['city']);
  if (city) {
    return uniqueCodes(city.split(/[,;|\/]+/));
  }

  return [];
}

function readFeatures(o: Record<string, unknown>): string[] {
  const raw = o.features ?? o.feature_list ?? o.featureList;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string' && item.trim()) return item.trim();
      if (item && typeof item === 'object') {
        const text = readString(item as Record<string, unknown>, ['text', 'name', 'label', 'title']);
        if (text) return text;
      }
      return null;
    })
    .filter((x): x is string => Boolean(x));
}

function annualPerMonth(monthly: number, yearly: number | undefined): number {
  if (yearly === undefined) return monthly;
  if (monthly > 0 && yearly > monthly * 1.5) return yearly / 12;
  return yearly;
}

function defaultPlanStyle(index: number, popular: boolean) {
  if (popular) {
    return {
      borderClass: 'border-green-500',
      cta: 'Start Free Trial',
      ctaClass: 'bg-green-600 text-white',
    };
  }
  if (index === 0) {
    return {
      borderClass: 'border-gray-200',
      cta: 'Start Free Trial',
      ctaClass: 'border border-green-500 text-green-600 bg-white',
    };
  }
  return {
    borderClass: 'border-gray-200',
    cta: 'Contact Sales',
    ctaClass: 'border border-gray-300 text-gray-600 bg-white',
  };
}

function normalizePlanRow(row: unknown, index: number): SubPricePlan | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;

  const monthly =
    readNum(o, ['price_month', 'monthly_price', 'monthlyPrice', 'month_price', 'price']) ?? 0;
  const yearlyRaw = readNum(o, ['price_year', 'yearly_price', 'yearlyPrice', 'year_price', 'annual_price']);
  const annualMonthly = annualPerMonth(monthly, yearlyRaw);

  const name = readString(o, ['name', 'plan_name', 'planName', 'title']) ?? `Plan ${index + 1}`;
  const id = readString(o, ['id', 'plan_id', 'planId']) ?? `plan-${index}`;
  const popular = o.popular === true || o.is_popular === 1 || o.is_popular === true;
  const style = defaultPlanStyle(index, popular);

  return {
    id,
    name,
    description:
      readString(o, ['description', 'desc', 'summary']) ??
      'Flexible subscription for your field service team.',
    monthlyPrice: monthly,
    annualMonthlyPrice: annualMonthly > 0 ? annualMonthly : monthly,
    features: readFeatures(o),
    badge: popular ? (readString(o, ['badge']) ?? 'Most Popular') : readString(o, ['badge']) ?? null,
    popular,
    ...style,
  };
}

function buildLandingPlan(params: {
  id: 'personal' | 'team';
  name: string;
  monthly: number;
  yearly?: number;
  popular?: boolean;
}): SubPricePlan {
  const annualMonthly = annualPerMonth(params.monthly, params.yearly);
  const popular = params.popular ?? false;
  const style = defaultPlanStyle(params.id === 'personal' ? 0 : 1, popular);

  return {
    id: params.id,
    name: params.name,
    description: '',
    monthlyPrice: params.monthly,
    annualMonthlyPrice: annualMonthly > 0 ? annualMonthly : params.monthly,
    features: [],
    badge: null,
    popular,
    ...style,
  };
}

/** 首页两种订阅：个人 / 团队 */
function normalizeLandingPlans(o: Record<string, unknown>): SubPricePlan[] {
  const personalMonth = readNum(o, ['personal_price_month', 'personalPriceMonth']);
  const personalYear = readNum(o, ['personal_price_year', 'personalPriceYear']);
  const teamMonth = readNum(o, ['team_price_month', 'teamPriceMonth', 'price_month', 'monthly_price', 'monthlyPrice']);
  const teamYear = readNum(o, ['team_price_year', 'teamPriceYear', 'price_year', 'yearly_price', 'yearlyPrice']);

  const personalMonthly = personalMonth ?? (personalYear !== undefined ? personalYear / 12 : 29);
  const teamMonthly = teamMonth ?? (teamYear !== undefined ? teamYear / 12 : 39);

  return [
    buildLandingPlan({
      id: 'personal',
      name: 'Personal',
      monthly: personalMonthly,
      yearly: personalYear,
    }),
    buildLandingPlan({
      id: 'team',
      name: 'Team',
      monthly: teamMonthly,
      yearly: teamYear,
      popular: true,
    }),
  ];
}

function normalizeFromPricePair(o: Record<string, unknown>): SubPricePlan[] {
  const monthly = readNum(o, ['price_month', 'monthly_price', 'monthlyPrice']);
  const yearly = readNum(o, ['price_year', 'yearly_price', 'yearlyPrice']);
  if (monthly === undefined && yearly === undefined) return [];

  return normalizeLandingPlans({
    ...o,
    team_price_month: monthly,
    team_price_year: yearly,
  });
}

function pickPlanRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  for (const k of ['list', 'plans', 'items', 'records', 'price_list', 'priceList']) {
    if (Array.isArray(o[k])) return o[k] as unknown[];
  }
  return [];
}

export function parseSubPricePlans(data: unknown): SubPricePlan[] {
  const rows = pickPlanRows(data);
  if (rows.length >= 2) {
    const parsed = rows
      .map((row, i) => normalizePlanRow(row, i))
      .filter((x): x is SubPricePlan => x !== null)
      .slice(0, 2);
    if (parsed.length >= 2) {
      return [
        { ...parsed[0], id: 'personal', name: 'Personal', popular: false, borderClass: 'border-gray-200', ctaClass: 'border border-green-500 text-green-600 bg-white' },
        { ...parsed[1], id: 'team', name: 'Team', popular: true, borderClass: 'border-green-500', ctaClass: 'bg-green-600 text-white' },
      ];
    }
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const hasPrice =
      readNum(o, ['personal_price_month', 'personalPriceMonth']) !== undefined
      || readNum(o, ['team_price_month', 'teamPriceMonth']) !== undefined
      || readNum(o, ['price_month', 'monthly_price', 'monthlyPrice']) !== undefined
      || readNum(o, ['price_year', 'yearly_price', 'yearlyPrice']) !== undefined;
    if (hasPrice) return normalizeLandingPlans(o);
    return normalizeFromPricePair(o);
  }
  return [];
}

export interface FetchTeamSubPriceParams {
  extraMembers?: number;
  /** 团队注册国家；未传入时默认 AU */
  country?: string | null;
}

/** POST `/system/team/get_sub_price` — 按国家取后台固定价（默认 AU，不做汇率换算） */
export async function fetchTeamSubPrice(params?: FetchTeamSubPriceParams): Promise<TeamSubPrice> {
  const country = normalizeTeamCountry(params?.country ?? DEFAULT_TEAM_COUNTRY);
  const body: Record<string, string | number> = { country };
  if (params?.extraMembers !== undefined && params.extraMembers !== null) {
    body.extra_members = params.extraMembers;
  }
  const res = await http.post<GetSubPriceHttpResponse>('/system/team/get_sub_price', body);
  const price = parseTeamSubPrice(pickCountryPriceData(res.data, country));
  if (!price) {
    throw new ApiRequestError('No subscription prices available.');
  }
  return price;
}

/** POST `/system/team/get_sub_price` — 订阅价格方案列表（首页定价区等） */
export async function fetchSubPricePlans(country?: string | null): Promise<SubPricePlan[]> {
  const resolved = normalizeTeamCountry(country ?? DEFAULT_TEAM_COUNTRY);
  const res = await http.post<GetSubPriceHttpResponse>('/system/team/get_sub_price', {
    country: resolved,
  });
  const plans = parseSubPricePlans(pickCountryPriceData(res.data, resolved));
  if (plans.length === 0) {
    throw new ApiRequestError('No subscription plans available.');
  }
  return plans;
}

interface TeamMutationResponse {
  error?: boolean;
  message?: string;
  team_id?: number | string;
  data?: unknown;
}

/** `postCreateTeam` 结果（含业务 `error` 字段，不抛错）。 */
export interface CreateTeamResult {
  error: boolean;
  message: string;
  teamId?: number | string;
}

function readTeamId(res: TeamMutationResponse): number | string | undefined {
  if (res.team_id !== undefined && res.team_id !== null && res.team_id !== '') {
    return res.team_id;
  }
  if (res.data && typeof res.data === 'object') {
    const d = res.data as Record<string, unknown>;
    const id = d.team_id ?? d.teamId ?? d.id;
    if (typeof id === 'number' || typeof id === 'string') return id;
  }
  return undefined;
}

/** POST `/system/team/create_team` 请求体 */
export interface CreateTeamBody {
  /** 团队名称（必填） */
  name: string;
  /** 团队 logo 路径 */
  logo_path?: string;
  /** 团队联系电话 */
  phone?: string;
  /** 团队联系邮箱 */
  email?: string;
  /** 团队地址 */
  address?: string;
  /** 注册地址国家或地区：AU / NZ / CA / UK / US */
  country?: string;
  /** 团队简介 */
  team_profile?: string;
  /** 团队状态，默认 1（正常） */
  state?: number;
}

interface UploadTeamLogoHttpResponse {
  error?: boolean;
  message?: string;
  data?: {
    /** Backend `upload_logo` returns this field */
    logo_url?: string;
    logo_path?: string;
    file_path?: string;
    url?: string;
  };
}

function readUploadedLogoPath(json: UploadTeamLogoHttpResponse): string | undefined {
  const d = json.data;
  if (!d) return undefined;
  const path = d.logo_url ?? d.logo_path ?? d.url ?? d.file_path;
  return typeof path === 'string' && path.trim() ? path.trim() : undefined;
}

/** `POST /system/team/admin_get_team_info` 响应 `data` */
export interface AdminTeamInfo {
  id: number | string;
  team_code?: string;
  name: string;
  logo_path?: string;
  phone?: string;
  email?: string;
  address?: string;
  /** 注册地址国家或地区：AU / NZ / CA / UK / US */
  country?: string;
  /** WhatsApp 号码（含国家区号） */
  whatsapp_number?: string;
  team_profile?: string;
  remarks?: string;
  admin_id?: number | string;
  create_date?: string;
  expire_time?: string | null;
  state?: number;
  member_count?: number;
  /** 税费税率（小数，0.1 表示 10%；0 表示 0%） */
  gst_rate?: number;
  /** 税号 / 商业登记号：ABN、IRD、EIN、CRN、BN */
  abn?: string | number;
  /** 税费名称，如 GST、VAT、Sales Tax、HST */
  tax_name?: string;
  /** 英国增值税号（未注册可空） */
  vat_number?: string;
  bank_name?: string;
  bank_account?: string;
  account_name?: string;
  /** 分行/路由代码：BSB、Sort Code、ABA、Bank & Branch、Institution+Transit */
  bank_bsb?: string;
  /** SWIFT / BIC，可选 */
  swift_code?: string;
  /** 英国 IBAN，跨国汇款用 */
  iban?: string;
  /** 发票税率概览：`invoice_info.tax_name` + `tax_rate` */
  invoice_info?: TeamInvoiceInfo;
  /** 基础套餐包含的成员名额（含团长） */
  base_members?: number;
  /** 已付费增购的额外坐席 */
  extra_members?: number;
  /** 1 表示当前处于免费试用 */
  is_trial_activated?: number;
  /** 当前坐席配额：基础 + 增购、已用、剩余 */
  member_capacity?: TeamMemberCapacity;
  subscription_info?: {
    expire_time?: string;
    months?: number;
    total_price?: number;
    base_price?: number;
    extra_members?: number;
    extra_member_unit_price?: number;
    subscription_type?: string;
  };
}

function parseAdminTeamInfo(data: unknown): AdminTeamInfo | null {
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  const name = readString(o, ['name', 'team_name']);
  if (!name) return null;
  const id = o.id ?? o.team_id ?? o.teamId;
  if (id === undefined || id === null || id === '') return null;

  const sub = o.subscription_info;
  let subscription_info: AdminTeamInfo['subscription_info'];
  if (sub && typeof sub === 'object') {
    const s = sub as Record<string, unknown>;
    subscription_info = {
      expire_time: readString(s, ['expire_time', 'expireTime']),
      months: readNum(s, ['months']),
      total_price: readNum(s, ['total_price', 'totalPrice']),
      base_price: readNum(s, ['base_price', 'basePrice']),
      extra_members: readNum(s, ['extra_members', 'extraMembers']),
      extra_member_unit_price: readNum(s, ['extra_member_unit_price', 'extraMemberUnitPrice']),
      subscription_type: readString(s, ['subscription_type', 'subscriptionType']),
    };
  }

  return {
    id: typeof id === 'number' || typeof id === 'string' ? id : String(id),
    team_code: readString(o, ['team_code', 'teamCode']),
    name,
    logo_path: readString(o, ['logo_path', 'logoPath']),
    phone: readString(o, ['phone']),
    email: readString(o, ['email']),
    address: readString(o, ['address']),
    country: readString(o, ['country']),
    whatsapp_number: readString(o, ['whatsapp_number', 'whatsappNumber']),
    team_profile: readString(o, ['team_profile', 'teamProfile']),
    remarks: readString(o, ['remarks']),
    admin_id: o.admin_id as number | string | undefined,
    create_date: readString(o, ['create_date', 'createDate']),
    expire_time: readString(o, ['expire_time', 'expireTime']) ?? null,
    state: readNum(o, ['state']),
    member_count: readNum(o, ['member_count', 'memberCount']),
    base_members: readNum(o, ['base_members', 'baseMembers']),
    extra_members: readNum(o, ['extra_members', 'extraMembers']),
    is_trial_activated: readNum(o, ['is_trial_activated', 'isTrialActivated']),
    member_capacity: parseTeamCapacity(o.member_capacity ?? o.memberCapacity) ?? undefined,
    gst_rate: readNum(o, ['gst_rate', 'gstRate']),
    abn: readString(o, ['abn']) ?? (readNum(o, ['abn']) !== undefined ? String(readNum(o, ['abn'])) : undefined),
    tax_name: readString(o, ['tax_name', 'taxName']),
    vat_number: readString(o, ['vat_number', 'vatNumber']),
    bank_name: readString(o, ['bank_name', 'bankName']),
    bank_account: readString(o, ['bank_account', 'bankAccount']),
    account_name: readString(o, ['account_name', 'accountName']),
    bank_bsb: readString(o, ['bank_bsb', 'bankBsb']),
    swift_code: readString(o, ['swift_code', 'swiftCode', 'swift']),
    iban: readString(o, ['iban']),
    invoice_info: parseTeamInvoiceInfo(o.invoice_info ?? o.invoiceInfo) ?? parseTeamInvoiceInfo(o),
    subscription_info,
  };
}

export interface TeamInvoiceInfo {
  tax_name?: string;
  tax_rate?: number;
}

function parseTeamInvoiceInfo(raw: unknown): TeamInvoiceInfo | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const tax_name = readString(o, ['tax_name', 'taxName']);
  const tax_rate = readNum(o, ['tax_rate', 'taxRate']);
  if (!tax_name && tax_rate === undefined) return undefined;
  return { tax_name, tax_rate };
}

/** 团队概览展示：`GST 10%`。`tax_rate` 为百分比；0–1 的小数按原 GST 税率换算。 */
export function formatInvoiceTaxLabel(info: TeamInvoiceInfo | null | undefined): string | null {
  if (!info) return null;
  const name = info.tax_name?.trim() ?? '';
  const hasRate = typeof info.tax_rate === 'number' && Number.isFinite(info.tax_rate);
  if (!name && !hasRate) return null;
  if (name && hasRate) return `${name} ${gstRateToPercentDisplay(info.tax_rate)}%`;
  if (hasRate) return `${gstRateToPercentDisplay(info.tax_rate)}%`;
  return name;
}

interface AdminTeamInfoHttpResponse {
  error?: boolean;
  message?: string;
  data?: unknown;
}

/** 团队服务人员（由 `/system/team/order/servicer/availablelist` 或 `admin_get_team_members` 归一化） */
export interface TeamMember {
  id: string;
  operatorId: number | string;
  name: string;
  avatar?: string;
  phone: string;
  email?: string;
  role: string;
  status: 'working' | 'resting';
  joinDate?: string;
  hourlyWage?: number;
  isOnline: boolean;
  /** 负责服务区域邮编 */
  postalCodes: string[];
}

export function formatTeamMemberHourlyWage(hourlyWage: number | undefined): string {
  if (typeof hourlyWage !== 'number' || !Number.isFinite(hourlyWage) || hourlyWage <= 0) {
    return 'Hourly wage not set';
  }
  return Number.isInteger(hourlyWage)
    ? `$${hourlyWage}/hr`
    : `$${hourlyWage.toFixed(2)}/hr`;
}

function parseAdminTeamMemberRow(row: unknown): TeamMember | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const operatorId = o.operator_id ?? o.operatorId ?? o.id;
  if (operatorId === undefined || operatorId === null || operatorId === '') return null;

  const name = readString(o, ['name']) ?? '—';
  const onlineRaw = o.online;
  const isOnline = onlineRaw === 1 || onlineRaw === '1' || onlineRaw === true;

  return {
    id: String(operatorId),
    operatorId: String(operatorId),
    name,
    avatar: readString(o, ['avatar']),
    phone: readString(o, ['mobile', 'phone', 'contact']) ?? '',
    email: readString(o, ['email', 'mail']),
    role: 'Team Member',
    status: isOnline ? 'working' : 'resting',
    joinDate: readString(o, ['create_date', 'createDate']),
    hourlyWage: readNum(o, ['hourly_wage', 'hourlyWage']),
    isOnline,
    postalCodes: parseMemberPostalCodes(o),
  };
}

function availableServicerToTeamMember(servicer: AvailableServicer): TeamMember {
  return {
    id: servicer.operatorId,
    operatorId: servicer.operatorId,
    name: servicer.name,
    avatar: servicer.avatar,
    phone: servicer.mobile ?? '',
    email: servicer.email,
    role: 'Team Member',
    status: servicer.isOnline ? 'working' : 'resting',
    joinDate: servicer.createDate,
    hourlyWage: servicer.hourlyWage,
    isOnline: servicer.isOnline,
    postalCodes: servicer.postalCodes ?? [],
  };
}

/** POST `/system/team/order/servicer/availablelist` — 分页获取团队可用服务人员 */
export async function fetchAdminTeamMembersPage(params: {
  page: number;
  pageSize: number;
  city?: string;
  serviceTime?: string;
  orderId?: number | string;
  postalCode?: string;
}): Promise<TeamListPageResult<TeamMember>> {
  const result = await fetchAvailableServicersPage({
    page: params.page,
    pageSize: params.pageSize,
    city: params.city,
    serviceTime: params.serviceTime,
    orderId: params.orderId,
    postalCode: params.postalCode,
  });
  return {
    items: result.items.map(availableServicerToTeamMember),
    total: result.total,
    page: result.page,
    pageSize: result.pageSize,
  };
}

/** @deprecated 使用 `fetchAdminTeamMembersPage` */
export async function fetchAdminTeamMembers(params?: {
  city?: string;
  serviceTime?: string;
  orderId?: number | string;
  postalCode?: string;
}): Promise<TeamMember[]> {
  const { items } = await fetchAdminTeamMembersPage({
    page: 1,
    pageSize: 1000,
    city: params?.city,
    serviceTime: params?.serviceTime,
    orderId: params?.orderId,
    postalCode: params?.postalCode,
  });
  return items;
}

export interface TeamListPageResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

function readPageTotal(res: { total?: number; data?: unknown }, itemsLength: number): number {
  if (typeof res.total === 'number' && res.total >= 0) return res.total;
  return itemsLength;
}

type ServicerContactExtras = {
  isOnline: boolean;
  phone: string;
  postalCodes: string[];
};

let inflightOnlineStatusMap: Promise<Map<string, ServicerContactExtras>> | null = null;

async function fetchTeamMemberOnlineStatusMap(): Promise<Map<string, ServicerContactExtras>> {
  if (inflightOnlineStatusMap) return inflightOnlineStatusMap;
  inflightOnlineStatusMap = fetchAvailableServicersPage({ page: 1, pageSize: 1000 })
    .then((result) => {
      const map = new Map<string, ServicerContactExtras>();
      for (const servicer of result.items) {
        map.set(servicer.operatorId, {
          isOnline: servicer.isOnline,
          phone: servicer.mobile ?? '',
          postalCodes: servicer.postalCodes ?? [],
        });
      }
      return map;
    })
    .finally(() => {
      inflightOnlineStatusMap = null;
    });
  return inflightOnlineStatusMap;
}

function mergeTeamMemberOnlineStatus(
  members: TeamMember[],
  extraMap: Map<string, ServicerContactExtras>,
): TeamMember[] {
  return members.map((member) => {
    const extra = extraMap.get(String(member.operatorId));
    if (!extra) return member;
    return {
      ...member,
      isOnline: extra.isOnline,
      status: extra.isOnline ? 'working' : 'resting',
      phone: member.phone || extra.phone,
      postalCodes: member.postalCodes.length > 0 ? member.postalCodes : extra.postalCodes,
    };
  });
}

interface AdminTeamMembersHttpResponse {
  error?: boolean;
  message?: string;
  data?: unknown[];
  total?: number;
  page?: number;
  page_size?: number;
}

/** POST `/system/team/admin_get_team_members` — 管理员获取团队成员（含 hourly_wage） */
export async function fetchTeamMembersAdminPage(params: {
  page: number;
  pageSize: number;
}): Promise<TeamListPageResult<TeamMember>> {
  const page = params.page;
  const pageSize = params.pageSize;

  const raw = await http.post<AdminTeamMembersHttpResponse>(
    '/system/team/admin_get_team_members',
    { page, page_size: pageSize },
    { skipApiEnvelopeModal: true },
  );

  if (raw.error === true) {
    throw new ApiRequestError(
      typeof raw.message === 'string' ? raw.message : 'Failed to load team members.',
    );
  }

  const rows = Array.isArray(raw.data) ? raw.data : [];
  const items = rows
    .map(parseAdminTeamMemberRow)
    .filter((item): item is TeamMember => item !== null);
  const onlineMap = await fetchTeamMemberOnlineStatusMap();

  return {
    items: mergeTeamMemberOnlineStatus(items, onlineMap),
    total: readPageTotal(raw, items.length),
    page: raw.page ?? page,
    pageSize: raw.page_size ?? pageSize,
  };
}

/** My Team 成员列表：优先 `admin_get_team_members` 获取时薪，并合并在线状态 */
export async function fetchJoinedTeamMembersPage(params: {
  page: number;
  pageSize: number;
}): Promise<TeamListPageResult<TeamMember>> {
  return fetchTeamMembersAdminPage(params);
}

/** `POST /system/team/get_pending_requests` 单条待审核申请 */
export interface PendingTeamRequest {
  id: string;
  teamName: string;
  applicant: string;
  /** 入团/退团目标成员 operator_id（邀请入团时为被邀请人） */
  operatorId?: string;
  hourlyWage?: number;
  createTime: string;
  requestType: number;
  requestTypeText: string;
}

function parsePendingRequestRow(row: unknown): PendingTeamRequest | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const id = o.id ?? o.request_id;
  if (id === undefined || id === null || id === '') return null;
  const requestType = readNum(o, ['request_type', 'requestType', 'type']) ?? 0;
  return {
    id: String(id),
    teamName: readString(o, ['team_name', 'teamName']) ?? '',
    applicant: readString(o, ['applicant', 'name', 'username']) ?? 'Unknown',
    operatorId: readString(o, ['operator_id', 'operatorId', 'applicant_id', 'applicantId']),
    hourlyWage: readNum(o, ['hourly_wage', 'hourlyWage']),
    createTime: readString(o, ['create_time', 'createTime']) ?? '',
    requestType,
    requestTypeText:
      readString(o, ['request_type_text', 'requestTypeText']) ?? 'Pending request',
  };
}

function pickRequestRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  for (const k of ['list', 'requests', 'items', 'records']) {
    if (Array.isArray(o[k])) return o[k] as unknown[];
  }
  return [];
}

interface PendingRequestsHttpResponse {
  error?: boolean;
  message?: string;
  data?: unknown;
  total?: number;
  page?: number;
  page_size?: number;
}

/** POST `/system/team/get_pending_requests` — 待审核申请分页列表 */
export async function fetchPendingTeamRequestsPage(params: {
  page: number;
  pageSize: number;
}): Promise<TeamListPageResult<PendingTeamRequest>> {
  const res = await http.post<PendingRequestsHttpResponse>('/system/team/get_pending_requests', {
    page: params.page,
    page_size: params.pageSize,
  });
  if (res.error === true) {
    throw new ApiRequestError(envelopeMessage(res, 'Failed to load pending requests.'));
  }
  const items = pickRequestRows(res.data)
    .map(parsePendingRequestRow)
    .filter((x): x is PendingTeamRequest => x !== null);
  return {
    items,
    total: readPageTotal(res, items.length),
    page: typeof res.page === 'number' ? res.page : params.page,
    pageSize: typeof res.page_size === 'number' ? res.page_size : params.pageSize,
  };
}

/** `POST /system/team/get_my_team_requests` 我提交的加入/退出申请 */
export interface MyTeamRequest {
  id: string;
  teamName: string;
  createTime: string;
  approveTime: string;
  requestType: number;
  requestTypeText: string;
  state: number;
  stateText: string;
  rejectReason?: string;
}

function parseMyTeamRequestRow(row: unknown): MyTeamRequest | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const id = o.id ?? o.request_id;
  if (id === undefined || id === null || id === '') return null;
  const requestType = readNum(o, ['request_type', 'requestType', 'type']) ?? 0;
  const state = readNum(o, ['state']) ?? 0;
  return {
    id: String(id),
    teamName: readString(o, ['team_name', 'teamName']) ?? '',
    createTime: readString(o, ['create_time', 'createTime']) ?? '',
    approveTime: readString(o, ['approve_time', 'approveTime']) ?? '',
    requestType,
    requestTypeText: readString(o, ['request_type_text', 'requestTypeText']) ?? '',
    state,
    stateText: readString(o, ['state_text', 'stateText']) ?? '',
    rejectReason: readString(o, ['reject_reason', 'rejectReason']) || undefined,
  };
}

/** POST `/system/team/get_my_team_requests` — 我的加入/退出申请记录 */
export async function fetchMyTeamRequestsPage(params: {
  page: number;
  pageSize: number;
}): Promise<TeamListPageResult<MyTeamRequest>> {
  const res = await http.post<PendingRequestsHttpResponse>(
    '/system/team/get_my_team_requests',
    {
      page: params.page,
      page_size: params.pageSize,
    },
    { skipApiEnvelopeModal: true },
  );
  if (res.error === true) {
    throw new ApiRequestError(envelopeMessage(res, 'Failed to load your requests.'));
  }
  const items = pickRequestRows(res.data)
    .map(parseMyTeamRequestRow)
    .filter((x): x is MyTeamRequest => x !== null);
  return {
    items,
    total: readPageTotal(res, items.length),
    page: typeof res.page === 'number' ? res.page : params.page,
    pageSize: typeof res.page_size === 'number' ? res.page_size : params.pageSize,
  };
}

/** POST `/system/team/admin_get_team_info` */
export async function fetchAdminTeamInfo(): Promise<AdminTeamInfo> {
  const res = await http.post<AdminTeamInfoHttpResponse>('/system/team/admin_get_team_info');
  if (res.error === true) {
    throw new ApiRequestError(envelopeMessage(res, 'Failed to load team info.'));
  }
  const info = parseAdminTeamInfo(res.data);
  if (!info) {
    throw new ApiRequestError('Failed to load team info.');
  }
  return info;
}

/** POST `/system/team/user_update_team` 请求体 */
export interface UpdateTeamBody {
  name: string;
  logo_path?: string;
  phone?: string;
  email?: string;
  address?: string;
  /** 注册地址国家或地区：AU / NZ / CA / UK / US */
  country?: string;
  team_profile?: string;
  remarks?: string;
  /** 税费税率（小数，0.1 = 10%，0 = 0%） */
  gst_rate: number;
  /** 税号 / 商业登记号：ABN、IRD、EIN、CRN、BN */
  abn: string | number;
  /** 税费名称，如 GST、VAT、Sales Tax、HST */
  tax_name?: string;
  /** 英国增值税号（未注册可空） */
  vat_number?: string;
  bank_name?: string;
  bank_account?: string;
  account_name?: string;
  /** 分行/路由代码 */
  bank_bsb?: string;
  /** SWIFT / BIC，可选 */
  swift_code?: string;
  /** 英国 IBAN，跨国汇款用 */
  iban?: string;
  /** WhatsApp 号码（含国家区号） */
  whatsapp_number?: string;
}

/** 将 API 返回的 GST 税率转为百分比显示（如 0.1 → "10"） */
export function gstRateToPercentDisplay(rate: number | undefined | null): string {
  if (rate === undefined || rate === null || !Number.isFinite(rate)) return '10';
  if (rate > 0 && rate <= 1) {
    const pct = rate * 100;
    return Number.isInteger(pct) ? String(pct) : String(Math.round(pct * 10) / 10);
  }
  return String(rate);
}

/** 将表单百分比转为 API 小数税率（10 → 0.1，0 → 0）。表单始终按百分比填写。 */
export function gstPercentInputToApiRate(percent: string): number {
  const n = Number(percent.trim());
  if (!Number.isFinite(n) || n < 0) return 0.1;
  if (n === 0) return 0;
  return Math.round(n * 10) / 1000;
}

/** 从团队信息构建更新请求体 */
export function buildUpdateTeamBodyFromAdminInfo(
  team: AdminTeamInfo,
  overrides: Partial<UpdateTeamBody> = {},
): UpdateTeamBody {
  return {
    name: team.name,
    logo_path: team.logo_path,
    phone: team.phone ?? '',
    email: team.email ?? '',
    address: team.address,
    country: team.country,
    team_profile: team.team_profile,
    remarks: team.remarks,
    gst_rate: team.gst_rate ?? 0.1,
    abn: team.abn ?? '',
    tax_name: team.tax_name,
    vat_number: team.vat_number,
    bank_name: team.bank_name,
    bank_account: team.bank_account,
    account_name: team.account_name,
    bank_bsb: team.bank_bsb,
    swift_code: team.swift_code,
    iban: team.iban,
    whatsapp_number: team.whatsapp_number,
    ...overrides,
  };
}

export interface UpdateTeamResult {
  error: boolean;
  message: string;
}

/** POST `/system/team/user_update_team` */
export async function postUserUpdateTeam(body: UpdateTeamBody): Promise<UpdateTeamResult> {
  const failFallback = 'Failed to update team.';
  const okFallback = 'Team updated successfully.';

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/user_update_team',
      {
        name: body.name.trim(),
        logo_path: body.logo_path?.trim() || undefined,
        phone: body.phone?.trim() || undefined,
        email: body.email?.trim() || undefined,
        address: body.address?.trim() || undefined,
        country: body.country?.trim() || undefined,
        team_profile: body.team_profile?.trim() || undefined,
        remarks: body.remarks?.trim() || undefined,
        gst_rate: body.gst_rate,
        abn: body.abn,
        tax_name: body.tax_name?.trim() || undefined,
        vat_number: body.vat_number?.trim() || undefined,
        bank_name: body.bank_name?.trim() || undefined,
        bank_account: body.bank_account?.trim() || undefined,
        account_name: body.account_name?.trim() || undefined,
        bank_bsb: body.bank_bsb?.trim() || undefined,
        swift_code: body.swift_code?.trim() || undefined,
        iban: body.iban?.trim() || undefined,
        whatsapp_number: body.whatsapp_number?.trim() ?? '',
      },
      { skipApiEnvelope: true },
    );

    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }

    return { error: false, message: envelopeMessage(res, okFallback) };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

export interface PreReviewInvoiceResult {
  message: string;
  invoiceBase64: string;
  invoiceType: 'pdf' | 'image';
  invoiceNumber?: string;
}

/** POST `/system/team/pre_review_invoice` — sample invoice PDF from saved team settings */
export async function postPreReviewInvoice(): Promise<PreReviewInvoiceResult> {
  const raw = await http.post<{
    error?: boolean;
    message?: string;
    data?: {
      invoice_base64?: string;
      invoiceBase64?: string;
      invoice_type?: string;
      invoiceType?: string;
      invoice_number?: string;
      invoiceNumber?: string;
    };
  }>('/system/team/pre_review_invoice', {}, { skipApiEnvelopeModal: true });

  if (raw.error === true) {
    throw new ApiRequestError(
      typeof raw.message === 'string' ? raw.message : 'Failed to preview invoice.',
    );
  }

  const base64 = raw.data?.invoice_base64 ?? raw.data?.invoiceBase64;
  if (!base64 || typeof base64 !== 'string') {
    throw new ApiRequestError('Invoice preview data is missing.');
  }

  const invoiceTypeRaw = raw.data?.invoice_type ?? raw.data?.invoiceType;
  const invoiceType = invoiceTypeRaw === 'image' ? 'image' : 'pdf';

  return {
    message: typeof raw.message === 'string' ? raw.message : 'Invoice preview generated.',
    invoiceBase64: base64,
    invoiceType,
    invoiceNumber: raw.data?.invoice_number ?? raw.data?.invoiceNumber,
  };
}

/** POST `/system/team/upload_logo` — multipart field `logo` */
export async function postUploadTeamLogo(file: Blob, filename = 'team-logo.jpg'): Promise<string> {
  const formData = new FormData();
  const name = file instanceof File && file.name ? file.name : filename;
  formData.append('logo', file, name);
  const { data: json } = await apiClient.post<UploadTeamLogoHttpResponse>(
    '/system/team/upload_logo',
    formData
  );
  const path = readUploadedLogoPath(json);
  if (!path) throw new ApiRequestError('Failed to upload logo.');
  return path;
}

/** POST `/system/team/create_team` */
export async function postCreateTeam(body: CreateTeamBody): Promise<CreateTeamResult> {
  const state = body.state ?? 1;
  const failFallback = 'Failed to create team.';
  const okFallback = 'Team created successfully.';

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/create_team',
      {
        name: body.name.trim(),
        logo_path: body.logo_path?.trim() || undefined,
        phone: body.phone?.trim() || undefined,
        email: body.email?.trim() || undefined,
        address: body.address?.trim() || undefined,
        country: body.country?.trim() || undefined,
        team_profile: body.team_profile?.trim() || undefined,
        state,
      },
      { skipApiEnvelope: true },
    );

    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }

    return {
      error: false,
      message: envelopeMessage(res, okFallback),
      teamId: readTeamId(res),
    };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

export interface ActivateFreeTrialResult {
  error: boolean;
  message: string;
}

/** POST `/system/team/activate_free_trial` — 首次创建团队时激活体验套餐（仅一次） */
export async function postActivateFreeTrial(): Promise<ActivateFreeTrialResult> {
  const failFallback = 'Failed to activate free trial.';
  const okFallback = 'Free trial activated successfully.';

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/activate_free_trial',
      {},
      { skipApiEnvelope: true },
    );
    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }
    return { error: false, message: envelopeMessage(res, okFallback) };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

/** POST `/system/team/apply_join_team_by_code` — 通过团队码申请加入团队 */
export async function postApplyJoinTeam(teamCode: string): Promise<void> {
  await http.post<TeamMutationResponse>(
    '/system/team/apply_join_team_by_code',
    { team_code: teamCode.trim() },
    { skipApiEnvelopeModal: true },
  );
}

export interface ApplyExitTeamResult {
  error: boolean;
  message: string;
}

export interface SwitchTeamResult {
  error: boolean;
  message: string;
  teamId?: number | string;
  teamName?: string;
  token?: string;
}

/** POST `/system/team/switch_team` — 切换当前活跃团队 */
export async function postSwitchTeam(teamId: number | string): Promise<SwitchTeamResult> {
  const failFallback = 'Failed to switch team.';
  const okFallback = 'Switched team successfully.';
  const idNum = Number(teamId);
  const payload = { team_id: Number.isFinite(idNum) ? idNum : teamId };

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/switch_team',
      payload,
      { skipApiEnvelope: true },
    );
    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }
    const data = res.data && typeof res.data === 'object'
      ? (res.data as Record<string, unknown>)
      : {};
    const token = typeof data.token === 'string' && data.token.trim() ? data.token.trim() : undefined;
    const name = typeof data.team_name === 'string' && data.team_name.trim()
      ? data.team_name.trim()
      : typeof data.teamName === 'string' && data.teamName.trim()
        ? data.teamName.trim()
        : undefined;
    return {
      error: false,
      message: envelopeMessage(res, okFallback),
      teamId: readTeamId(res) ?? teamId,
      teamName: name,
      token,
    };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

/** POST `/system/team/op/apply_exit_team` — 工作人员申请退出团队 */
export async function postApplyExitTeam(
  teamId?: number | string,
): Promise<ApplyExitTeamResult> {
  const failFallback = 'Failed to submit exit request.';
  const okFallback = 'Exit request submitted.';
  const payload =
    teamId !== undefined && teamId !== null && String(teamId).trim() !== ''
      ? { team_id: String(teamId) }
      : {};

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/op/apply_exit_team',
      payload,
      { skipApiEnvelope: true },
    );
    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }
    return { error: false, message: envelopeMessage(res, okFallback) };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

/** 处理团队申请：1 同意，2 拒绝 */
export type HandleTeamRequestAction = 1 | 2;

export interface HandleTeamRequestResult {
  error: boolean;
  message: string;
}

/** POST `/system/team/handle_request` — 处理团队申请（同意 action=1 / 拒绝 action=2） */
export async function postHandleTeamRequest(
  requestId: string | number,
  action: HandleTeamRequestAction,
  hourlyWage?: number,
  rejectReason?: string,
): Promise<HandleTeamRequestResult> {
  const failFallback = action === 1 ? 'Failed to approve request.' : 'Failed to reject request.';
  const okFallback = action === 1 ? 'Request approved.' : 'Request rejected.';
  const id = Number(requestId);
  if (!Number.isInteger(id) || id <= 0) {
    return { error: true, message: 'Unable to process request: request id is missing.' };
  }

  const payload: Record<string, string | number> = {
    request_id: id,
    action,
  };
  if (typeof hourlyWage === 'number' && Number.isFinite(hourlyWage) && hourlyWage > 0) {
    payload.hourly_wage = hourlyWage;
  }
  const reason = rejectReason?.trim();
  if (reason) {
    payload.reject_reason = reason.slice(0, 200);
  }

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/handle_request',
      payload,
      { skipApiEnvelopeModal: true },
    );

    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }

    return { error: false, message: envelopeMessage(res, okFallback) };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

export interface AdminInviteUserResult {
  error: boolean;
  message: string;
}

/** POST `/system/team/admin_invite_user` — 管理员按邮箱邀请用户加入团队 */
export async function postAdminInviteUser(email: string): Promise<AdminInviteUserResult> {
  const failFallback = 'Failed to send invitation.';
  const okFallback = 'Invitation sent successfully.';

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/admin_invite_user',
      { email: email.trim() },
      { skipApiEnvelope: true },
    );

    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }

    return { error: false, message: envelopeMessage(res, okFallback) };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

/** 订阅单位：0 月，1 年（与后端 `create_sub_order` 一致） */
export type SubOrderUnit = 0 | 1;

export interface CreateSubOrderResult {
  orderId: number | string;
  totalPrice: number;
  months: number;
  startTime?: string;
  expireTime?: string;
}

interface CreateSubOrderHttpResponse {
  error?: boolean;
  message?: string;
  data?: Record<string, unknown>;
}

function readOrderId(data: Record<string, unknown> | undefined): number | string | undefined {
  if (!data) return undefined;
  const id = data.order_id ?? data.orderId ?? data.id;
  if (typeof id === 'number' && Number.isFinite(id)) return id;
  if (typeof id === 'string' && id.trim()) return id.trim();
  return undefined;
}

/** POST `/system/team/create_sub_order` — 创建订阅订单 */
export async function postCreateSubOrder(params: {
  number: number;
  unit: SubOrderUnit;
  extraMembers?: number;
}): Promise<CreateSubOrderResult> {
  const body: Record<string, number> = {
    number: params.number,
    unit: params.unit,
  };
  if (params.extraMembers !== undefined) {
    body.extra_members = params.extraMembers;
  }
  const res = await http.post<CreateSubOrderHttpResponse>('/system/team/create_sub_order', body);
  const payload = res.data && typeof res.data === 'object' ? res.data : {};
  const orderId = readOrderId(payload);
  if (orderId === undefined) {
    throw new ApiRequestError('Subscription order id missing in response.');
  }
  const total =
    typeof payload.total_price === 'number'
      ? payload.total_price
      : typeof payload.total_price === 'string' && !Number.isNaN(Number(payload.total_price))
        ? Number(payload.total_price)
        : 0;
  const months =
    typeof payload.months === 'number'
      ? payload.months
      : typeof payload.months === 'string' && !Number.isNaN(Number(payload.months))
        ? Number(payload.months)
        : 0;
  return {
    orderId,
    totalPrice: total,
    months,
    startTime:
      typeof payload.start_time === 'string' ? payload.start_time : undefined,
    expireTime:
      typeof payload.expire_time === 'string' ? payload.expire_time : undefined,
  };
}

export interface CreateExtraMemberOrderResult {
  orderId: number | string;
  extraMembers: number;
  extraMemberPriceMonth: number;
  months: number;
  totalPrice: number;
  startTime?: string;
  expireTime?: string;
}

/** POST `/system/team/create_extra_member_order` — 单独增购人数订单 */
export async function postCreateExtraMemberOrder(extraMembers: number): Promise<CreateExtraMemberOrderResult> {
  const res = await http.post<CreateSubOrderHttpResponse>('/system/team/create_extra_member_order', {
    extra_members: extraMembers,
  });
  const payload = res.data && typeof res.data === 'object' ? res.data : {};
  const orderId = readOrderId(payload);
  if (orderId === undefined) {
    throw new ApiRequestError('Extra member order id missing in response.');
  }
  const total =
    typeof payload.total_price === 'number'
      ? payload.total_price
      : typeof payload.total_price === 'string' && !Number.isNaN(Number(payload.total_price))
        ? Number(payload.total_price)
        : 0;
  const months =
    typeof payload.months === 'number'
      ? payload.months
      : typeof payload.months === 'string' && !Number.isNaN(Number(payload.months))
        ? Number(payload.months)
        : 0;
  const extra =
    typeof payload.extra_members === 'number'
      ? payload.extra_members
      : extraMembers;
  const unitPrice =
    typeof payload.extra_member_price_month === 'number'
      ? payload.extra_member_price_month
      : 0;
  return {
    orderId,
    extraMembers: extra,
    extraMemberPriceMonth: unitPrice,
    months,
    totalPrice: total,
    startTime: typeof payload.start_time === 'string' ? payload.start_time : undefined,
    expireTime: typeof payload.expire_time === 'string' ? payload.expire_time : undefined,
  };
}

/** 团队清洁项目 `clear_type` 分类 */
export const TEAM_CLEAN_TYPES = ['airbnb', 'office', 'others', 'standard'] as const;
export type TeamCleanType = typeof TEAM_CLEAN_TYPES[number];

export const TEAM_CLEAN_TYPE_LABELS: Record<TeamCleanType, string> = {
  airbnb: 'Airbnb',
  office: 'Office',
  others: 'Others',
  standard: 'Standard',
};

/** 后端 `clear_type` 整型 ↔ 前端分类字符串（与订单 cleaning_type 顺序一致） */
export const TEAM_CLEAN_TYPE_TO_INT: Record<TeamCleanType, number> = {
  standard: 0,
  office: 1,
  airbnb: 2,
  others: 3,
};

const TEAM_CLEAN_INT_TO_TYPE: Record<number, TeamCleanType> = {
  0: 'standard',
  1: 'office',
  2: 'airbnb',
  3: 'others',
};

export function teamCleanTypeToInt(clearType: TeamCleanType): number {
  return TEAM_CLEAN_TYPE_TO_INT[clearType];
}

function parseTeamCleanTypeValue(value: unknown): TeamCleanType | '' {
  if (typeof value === 'number' && Number.isInteger(value) && value in TEAM_CLEAN_INT_TO_TYPE) {
    return TEAM_CLEAN_INT_TO_TYPE[value];
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if ((TEAM_CLEAN_TYPES as readonly string[]).includes(trimmed)) {
      return trimmed as TeamCleanType;
    }
    const asInt = Number(trimmed);
    if (Number.isInteger(asInt) && asInt in TEAM_CLEAN_INT_TO_TYPE) {
      return TEAM_CLEAN_INT_TO_TYPE[asInt];
    }
  }
  return '';
}

/** 团队清洁项目 */
export interface TeamCleanItem {
  id: string;
  itemName: string;
  description: string;
  unit: string;
  unitPrice: number;
  /** 单项工时（小时） */
  duration?: number;
  clearType: TeamCleanType | '';
  enabled: boolean;
  createDate: string;
  updateDate: string;
}

function parseTeamCleanItemRow(row: unknown): TeamCleanItem | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const id = o.id ?? o.item_id;
  if (id === undefined || id === null || id === '') return null;
  const enabledRaw = o.enabled;
  const enabled =
    enabledRaw === 1 || enabledRaw === true || enabledRaw === '1';
  const clearTypeRaw = o.clear_type ?? o.clearType;
  return {
    id: String(id),
    itemName: readString(o, ['item_name', 'itemName']) ?? 'Untitled',
    description: readString(o, ['description']) ?? '',
    unit: readString(o, ['unit', 'item_unit']) ?? '',
    unitPrice: readNum(o, ['unit_price', 'unitPrice', 'item_price']) ?? 0,
    duration: readNum(o, ['duration']),
    clearType: parseTeamCleanTypeValue(clearTypeRaw),
    enabled,
    createDate: readString(o, ['create_date', 'createDate']) ?? '',
    updateDate: readString(o, ['update_date', 'updateDate']) ?? '',
  };
}

function pickCleanItemRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  if (Array.isArray(o.items)) return o.items as unknown[];
  for (const k of ['list', 'records']) {
    if (Array.isArray(o[k])) return o[k] as unknown[];
  }
  return [];
}

interface TeamCleanItemsHttpResponse {
  error?: boolean;
  message?: string;
  data?: unknown;
}

/** POST `/system/team/get_clean_items` — 按清洁类型获取团队清洁项目列表 */
export async function fetchTeamCleanItems(clearType: TeamCleanType): Promise<TeamCleanItem[]> {
  const res = await http.post<TeamCleanItemsHttpResponse>('/system/team/get_clean_items', {
    clear_type: teamCleanTypeToInt(clearType),
  });
  if (res.error === true) {
    throw new ApiRequestError(envelopeMessage(res, 'Failed to load clean items.'));
  }
  return pickCleanItemRows(res.data)
    .map(parseTeamCleanItemRow)
    .filter((x): x is TeamCleanItem => x !== null);
}

export interface CleanItemMutationBody {
  itemName: string;
  description?: string;
  unit?: string;
  unitPrice: number;
  /** 单项工时（小时） */
  duration: number;
  clearType: TeamCleanType;
  enabled: boolean;
}

export interface CleanItemMutationResult {
  error: boolean;
  message: string;
  item?: TeamCleanItem;
}

function parseCleanItemMutationResult(
  res: TeamMutationResponse,
  failFallback: string,
  okFallback: string,
): CleanItemMutationResult {
  if (res.error === true) {
    return { error: true, message: envelopeMessage(res, failFallback) };
  }
  const item = res.data ? parseTeamCleanItemRow(res.data) : null;
  return {
    error: false,
    message: envelopeMessage(res, okFallback),
    item: item ?? undefined,
  };
}

/** POST `/system/team/add_clean_item` — 添加团队清洁项目 */
export async function postAddCleanItem(body: CleanItemMutationBody): Promise<CleanItemMutationResult> {
  const failFallback = 'Failed to add clean item.';
  const okFallback = 'Clean item added successfully.';

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/add_clean_item',
      {
        item_name: body.itemName.trim(),
        description: body.description?.trim() || undefined,
        unit: body.unit?.trim() || 'H',
        unit_price: body.unitPrice,
        duration: body.duration,
        clear_type: teamCleanTypeToInt(body.clearType),
        enabled: body.enabled ? 1 : 0,
      },
      { skipApiEnvelope: true },
    );
    return parseCleanItemMutationResult(res, failFallback, okFallback);
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

/** POST `/system/team/update_clean_item` — 更新团队清洁项目 */
export async function postUpdateCleanItem(
  itemId: string | number,
  body: CleanItemMutationBody,
): Promise<CleanItemMutationResult> {
  const failFallback = 'Failed to update clean item.';
  const okFallback = 'Clean item updated successfully.';

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/update_clean_item',
      {
        item_id: Number(itemId),
        item_name: body.itemName.trim(),
        description: body.description?.trim() || undefined,
        unit: body.unit?.trim() || 'H',
        unit_price: body.unitPrice,
        duration: body.duration,
        clear_type: teamCleanTypeToInt(body.clearType),
        enabled: body.enabled ? 1 : 0,
      },
      { skipApiEnvelope: true },
    );
    return parseCleanItemMutationResult(res, failFallback, okFallback);
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

/** 提现申请状态：0 待处理，1 已通过，2 已拒绝 */
export type WithdrawalStatus = 0 | 1 | 2;

/** `POST /system/team/get_withdrawal_list` 单条提现申请 */
export interface TeamWithdrawalRecord {
  id: string;
  operatorId: number | string;
  operatorName: string;
  amount: number;
  withdrawalFee: number;
  actualAmount: number;
  bankAccountName?: string;
  bankCardNumber?: string;
  bankName?: string;
  submitTime?: string;
  auditTime?: string;
  auditRemark?: string;
  status: WithdrawalStatus;
}

export const WITHDRAWAL_STATUS_LABELS: Record<WithdrawalStatus, string> = {
  0: 'Pending',
  1: 'Approved',
  2: 'Rejected',
};

function parseWithdrawalRow(row: unknown): TeamWithdrawalRecord | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const id = o.id ?? o.withdrawal_id;
  if (id === undefined || id === null || id === '') return null;
  const statusRaw = readNum(o, ['status']);
  const status = (statusRaw === 0 || statusRaw === 1 || statusRaw === 2 ? statusRaw : 0) as WithdrawalStatus;
  const operatorId = o.operator_id ?? o.operatorId;
  return {
    id: String(id),
    operatorId:
      operatorId !== undefined && operatorId !== null && operatorId !== ''
        ? typeof operatorId === 'number' || typeof operatorId === 'string'
          ? operatorId
          : String(operatorId)
        : '',
    operatorName: readString(o, ['operator_name', 'operatorName']) ?? 'Unknown',
    amount: readNum(o, ['amount']) ?? 0,
    withdrawalFee: readNum(o, ['withdrawal_fee', 'withdrawalFee']) ?? 0,
    actualAmount: readNum(o, ['actual_amount', 'actualAmount']) ?? 0,
    bankAccountName: readString(o, ['bank_account_name', 'bankAccountName']),
    bankCardNumber: readString(o, ['bank_card_number', 'bankCardNumber']),
    bankName: readString(o, ['bank_name', 'bankName']),
    submitTime: readString(o, ['submit_time', 'submitTime']),
    auditTime: readString(o, ['audit_time', 'auditTime']),
    auditRemark: readString(o, ['audit_remark', 'auditRemark']),
    status,
  };
}

function pickWithdrawalRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  for (const k of ['list', 'records', 'items']) {
    if (Array.isArray(o[k])) return o[k] as unknown[];
  }
  return [];
}

interface WithdrawalListHttpResponse {
  error?: boolean;
  message?: string;
  data?: unknown;
  total?: number;
  page?: number;
  page_size?: number;
}

function readTotalPages(total: number, pageSize: number): number {
  if (total <= 0 || pageSize <= 0) return 0;
  return Math.ceil(total / pageSize);
}

/** POST `/system/team/get_withdrawal_list` — 提现申请分页列表（团队管理员） */
export async function fetchWithdrawalListPage(params: {
  page: number;
  pageSize: number;
  status?: WithdrawalStatus;
}): Promise<TeamListPageResult<TeamWithdrawalRecord> & { totalPages: number }> {
  const body: Record<string, unknown> = {
    page: params.page,
    page_size: params.pageSize,
  };
  if (params.status !== undefined) {
    body.status = params.status;
  }

  const res = await http.post<WithdrawalListHttpResponse>('/system/team/get_withdrawal_list', body);
  if (res.error === true) {
    throw new ApiRequestError(envelopeMessage(res, 'Failed to load withdrawal list.'));
  }

  const items = pickWithdrawalRows(res.data)
    .map(parseWithdrawalRow)
    .filter((x): x is TeamWithdrawalRecord => x !== null);
  const total = readPageTotal(res, items.length);
  const pageSize = typeof res.page_size === 'number' ? res.page_size : params.pageSize;

  return {
    items,
    total,
    page: typeof res.page === 'number' ? res.page : params.page,
    pageSize,
    totalPages: readTotalPages(total, pageSize),
  };
}

/** 订阅订单状态：0 待支付，1 已支付 */
export type TeamSubscriptionOrderStatus = 0 | 1;

/** 订阅类型：基础订阅/续订、增购坐席、试用 */
export type TeamSubscriptionType = 'subscription' | 'extra_members' | 'trial';

/** `POST /system/team/admin_get_team_subscriptions` 单条订阅订单 */
export interface TeamSubscriptionOrder {
  id: string;
  status: TeamSubscriptionOrderStatus;
  paymentMethod?: string;
  paymentTime?: string;
  totalPrice: number;
  months: number;
  startTime?: string;
  expireTime?: string;
  createTime?: string;
  email?: string;
  basePrice?: number;
  extraMembers: number;
  extraMemberUnitPrice?: number;
  subscriptionType: string;
  baseMembers?: number;
}

function parseSubscriptionOrder(row: unknown): TeamSubscriptionOrder | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const id = o.id ?? o.order_id ?? o.orderId;
  if (id === undefined || id === null || id === '') return null;
  const statusRaw = readNum(o, ['status']);
  const status = (statusRaw === 1 ? 1 : 0) as TeamSubscriptionOrderStatus;
  return {
    id: String(id),
    status,
    paymentMethod: readString(o, ['payment_method', 'paymentMethod']),
    paymentTime: readString(o, ['payment_time', 'paymentTime']),
    totalPrice: readNum(o, ['total_price', 'totalPrice']) ?? 0,
    months: readNum(o, ['months']) ?? 0,
    startTime: readString(o, ['start_time', 'startTime']),
    expireTime: readString(o, ['expire_time', 'expireTime']),
    createTime: readString(o, ['create_time', 'createTime']),
    email: readString(o, ['email']),
    basePrice: readNum(o, ['base_price', 'basePrice']),
    extraMembers: readNum(o, ['extra_members', 'extraMembers']) ?? 0,
    extraMemberUnitPrice: readNum(o, ['extra_member_unit_price', 'extraMemberUnitPrice']),
    subscriptionType: readString(o, ['subscription_type', 'subscriptionType']) ?? 'subscription',
    baseMembers: readNum(o, ['base_members', 'baseMembers']),
  };
}

/** POST `/system/team/admin_get_team_subscriptions` — 团长查询本团队订阅订单 */
export async function fetchAdminTeamSubscriptions(params: {
  page: number;
  pageSize: number;
  status?: TeamSubscriptionOrderStatus;
  subscriptionType?: TeamSubscriptionType;
}): Promise<TeamListPageResult<TeamSubscriptionOrder> & { totalPages: number }> {
  const body: Record<string, unknown> = {
    page: params.page,
    page_size: params.pageSize,
  };
  if (params.status !== undefined) {
    body.status = params.status;
  }
  if (params.subscriptionType) {
    body.subscription_type = params.subscriptionType;
  }

  const res = await http.post<WithdrawalListHttpResponse>(
    '/system/team/admin_get_team_subscriptions',
    body,
  );
  if (res.error === true) {
    throw new ApiRequestError(envelopeMessage(res, 'Failed to load subscription orders.'));
  }

  const items = pickWithdrawalRows(res.data)
    .map(parseSubscriptionOrder)
    .filter((x): x is TeamSubscriptionOrder => x !== null);
  const total = readPageTotal(res, items.length);
  const pageSize = typeof res.page_size === 'number' ? res.page_size : params.pageSize;

  return {
    items,
    total,
    page: typeof res.page === 'number' ? res.page : params.page,
    pageSize,
    totalPages: readTotalPages(total, pageSize),
  };
}

/** 审核提现：1 通过，2 拒绝（拒绝时 remark 必填） */
export type AuditWithdrawalAction = 1 | 2;

export interface AuditWithdrawalResult {
  error: boolean;
  message: string;
}

/** POST `/system/team/audit_withdrawal` — 管理员审核提现申请 */
export async function postAuditWithdrawal(params: {
  withdrawalId: string | number;
  status: AuditWithdrawalAction;
  auditRemark?: string;
}): Promise<AuditWithdrawalResult> {
  const isReject = params.status === 2;
  const failFallback = isReject
    ? 'Failed to reject withdrawal application.'
    : 'Failed to approve withdrawal application.';
  const okFallback = isReject
    ? 'Withdrawal application rejected.'
    : 'Withdrawal application approved.';

  try {
    const formData = new FormData();
    formData.append('withdrawal_id', String(Number(params.withdrawalId)));
    formData.append('status', String(params.status));
    if (params.auditRemark?.trim()) {
      formData.append('audit_remark', params.auditRemark.trim());
    }

    const res = await http.post<TeamMutationResponse>(
      '/system/team/audit_withdrawal',
      formData,
      { skipApiEnvelope: true },
    );

    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }

    return { error: false, message: envelopeMessage(res, okFallback) };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

/** POST `/system/team/delete_clean_item` — 删除团队清洁项目 */
export async function postDeleteCleanItem(itemId: string | number): Promise<CleanItemMutationResult> {
  const failFallback = 'Failed to delete clean item.';
  const okFallback = 'Clean item deleted successfully.';

  try {
    const res = await http.post<TeamMutationResponse>(
      '/system/team/delete_clean_item',
      { item_id: Number(itemId) },
      { skipApiEnvelope: true },
    );
    return parseCleanItemMutationResult(res, failFallback, okFallback);
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}

export interface UpdateServicerHourlyWageBody {
  operatorId: number | string;
  hourlyWage: number;
}

export interface UpdateServicerHourlyWageResult {
  message: string;
  hourlyWage: number;
}

/** POST `/system/team/update_servicer_hourly_wage` — 修改服务人员时薪 */
export async function postUpdateServicerHourlyWage(
  body: UpdateServicerHourlyWageBody,
): Promise<UpdateServicerHourlyWageResult> {
  if (body.hourlyWage <= 0) {
    throw new ApiRequestError('Hourly wage must be greater than 0.');
  }

  const formData = new FormData();
  formData.append('operator_id', String(body.operatorId));
  formData.append('hourly_wage', String(body.hourlyWage));

  const raw = await http.post<{
    error?: boolean;
    message?: string;
    data?: { hourly_wage?: number };
  }>(
    '/system/team/update_servicer_hourly_wage',
    formData,
    { skipApiEnvelopeModal: true },
  );

  if (raw.error === true) {
    throw new ApiRequestError(
      typeof raw.message === 'string' ? raw.message : 'Failed to update hourly wage.',
    );
  }

  const hourlyWage = raw.data?.hourly_wage ?? body.hourlyWage;
  return {
    message: typeof raw.message === 'string' ? raw.message : 'Hourly wage updated successfully.',
    hourlyWage,
  };
}
